import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useCallback, useState } from 'react';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { PaginatedResponse } from '@interfaces/machines';
import { OperationSearchItem, PartSearchItem } from '@interfaces/search';
import { useSelector } from 'react-redux';
import {
  selectForceUpdateHeaderConfig,
  setJobId,
  setJobs,
  setNcFileId,
  setNcFiles,
  setParts,
  setPartId,
  setNcFileData,
} from '../../../../../reducers/details';
import { GetBatchesByEntityId, GetNCFilesByEntityId, LoadSingleBatch, SearchEntity } from '@interfaces/methods';
import { parseRouteParamAsNumber } from '@helpers';
import { setBatchId } from '@reducers/batch';
import { useAppDispatch } from '@store/hooks/useAppDispatch';

export type OperationSearch = (params: {
  data: Record<string, unknown>;
  filter?: Record<string, unknown>;
  select?: string[] | null;
  resetHistory?: boolean;
  signal?: AbortSignal;
  ignoreHistory?: boolean;
}) => Promise<PaginatedResponse<OperationSearchItem>>;

interface UseJobDetailsProps {
  jobId?: number;
  operationsSearch: SearchEntity<OperationSearchItem>;
  getNCFilesByProductionOperationId?: GetNCFilesByEntityId;
  updateHeaderConfig?: (headerConfig: HeaderConfig) => void;
  getProductionBatchByProductionOperationId?: GetBatchesByEntityId;
  getAllNcFiles?: boolean;
  loadSingleBatch?: LoadSingleBatch;
}

export const useJobDetails = ({
  jobId: _jobId,
  operationsSearch,
  updateHeaderConfig,
  getNCFilesByProductionOperationId,
  getProductionBatchByProductionOperationId,
  getAllNcFiles,
  loadSingleBatch,
}: UseJobDetailsProps) => {
  const route = useRoute();
  const dispatch = useAppDispatch();
  const forceUpdateHeaderConfig = useSelector(selectForceUpdateHeaderConfig);

  const [ncFileDataLoading, setNcFileDataLoading] = useState<boolean>(true);

  const jobId = _jobId ?? parseRouteParamAsNumber(route.params?.productionOperationId);
  const keepList = route?.params?.keepList;

  const loadAndSetNcFileData = async () => {
    if (jobId == null || !getNCFilesByProductionOperationId) {
      setNcFileDataLoading(false);
      return;
    }

    const ncFileData = await getNCFilesByProductionOperationId(jobId, !getAllNcFiles ? { top: 1 } : null);
    if (ncFileData?.searchResult) {
      if (ncFileData?.searchResult?.length === 1) {
        dispatch(setNcFileData(ncFileData?.searchResult?.[0]));
        dispatch(setNcFileId(ncFileData?.searchResult?.[0]?.ncFileId));
      }
      dispatch(setNcFiles(ncFileData.searchResult));
    }
    setNcFileDataLoading(false);
    return ncFileData;
  };

  const setPartData = (jobData: OperationSearchItem) => {
    const part: PartSearchItem = {
      createdBy: jobData.createdBy,
      createdOn: jobData.createdOn,
      customerName: jobData.customerName,
      description: jobData.partDescription,
      drawing: jobData.partDrawingNumber,
      drawingRevisionPosition: jobData.partDrawingRevision + jobData.partDrawingPosition,
      imageData: jobData.imageData,
      imageDocumentId: jobData.imageDocumentId,
      imageHash: jobData.imageHash,
      linkedNcFiles: jobData.linkedNcFiles,
      modifiedBy: jobData.modifiedBy,
      modifiedOn: jobData.modifiedOn,
      partId: jobData.partId,
      partName: jobData.partNumber,
      position: jobData.partDrawingPosition,
      revision: jobData.partDrawingRevision,
      score: jobData.score,
    };
    dispatch(setParts([part]));
    dispatch(setPartId(part?.partId));
  };

  const loadBatches = async () => {
    if (!getProductionBatchByProductionOperationId) return;
    const batches = await getProductionBatchByProductionOperationId({ id: jobId, select: ['ProductionBatchId', 'BatchNumber'], filters: { top: 1 } });
    if (batches?.totalResultCount === 1) {
      const loadedBatchId = batches.searchResult?.[0]?.productionBatchId;
      dispatch(setBatchId(loadedBatchId || null));
      await loadSingleBatch?.(loadedBatchId);
    }
    return batches;
  };

  const loadAndSetJobs = async () => {
    const jobsData = await operationsSearch({
      data: {},
      filter: { oDataFilter: `ProductionOperationId eq ${jobId}` },
      select: null,
      resetHistory: true,
      ignoreHistory: true,
    });

    if (jobsData?.searchResult) {
      dispatch(setJobs(jobsData.searchResult));
      if (jobsData.searchResult?.length === 1) {
        dispatch(setJobId(jobsData.searchResult?.[0]?.productionOperationId));
      }
    }

    return jobsData?.searchResult;
  };

  const loadDataAndDefineHeader = async () => {
    const jobsData = await loadAndSetJobs();

    if (jobsData) {
      setPartData(jobsData[0]);
    }

    const batches = await loadBatches();
    const ncFiles = await loadAndSetNcFileData();

    const ncFileHeader = {
      ncFile: ncFiles?.totalResultCount === 1 ? ncFiles?.searchResult?.[0].ncFileName : `#${ncFiles?.totalResultCount ?? 0}`,
      ncFileId: ncFiles?.totalResultCount === 1 ? ncFiles?.searchResult?.[0]?.ncFileId : '',
      ncFileStatus: ncFiles?.totalResultCount === 1 ? ncFiles?.searchResult?.[0]?.ncFileStatus : '',
    };

    const batchHeader = batches?.totalResultCount === 1 ? batches?.searchResult?.[0]?.batchNumber : `#${batches?.totalResultCount ?? 0}`;

    if (updateHeaderConfig) {
      updateHeaderConfig({
        job: `${jobsData?.[0]?.productionOperationNumber}`,
        batch: batchHeader,
        ...ncFileHeader,
        part: {
          name: jobsData?.[0]?.partNumber ?? '',
          id: '',
        },
        productionOrder: '',
        tools: '#0',
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const fetchData = async () => {
        if (isActive) {
          await loadDataAndDefineHeader();
        }
      };

      fetchData();

      return () => {
        isActive = false;
      };
    }, [jobId, forceUpdateHeaderConfig, keepList])
  );

  return {
    ncFileDataLoading,
  };
};
